// Copyright (c) 2025 WiseTime. All rights reserved.

import { concat, Observable, of } from "rxjs"
import { never } from "grpc-web-rx"
import { Action } from "redux"
import { t } from "@lingui/macro"
import { catchError, filter, map, mergeMap, withLatestFrom } from "rxjs/operators"
import { StatusCode } from "grpc-web"
import * as Slice from "../slices/timeline"
import * as Actions from "../actions"
import { grpc } from "../../../app/rpc/executor"
import { showSnackBar } from "../../snackbar/actions"
import { timelineClient } from "../../../app/rpc/client"
import { isGrpcError } from "../../../lib/rpcutils"
import {
  SelectTimesheetFieldOptionRequest,
  TimeGroup,
} from "../../../generated/wisetime/user/timeline/v4/timeline_pb"
import { RootState } from "../../../app/store"
import { getTimesheetFieldLabel } from "../timesheet/timesheet-fields"
import {
  ListRecentTimesheetFieldOptionsRequest
} from "../../../generated/wisetime/site/timesheetfield/v1/timesheet_field_pb"

type SelectTimesheetFieldOptionRpc = (request: SelectTimesheetFieldOptionRequest) => Observable<TimeGroup>

export const selectTimesheetFieldOption = (selectTimesheetFieldOptionRpc: SelectTimesheetFieldOptionRpc) =>
  (action$: Observable<Action>, state$: Observable<RootState>): Observable<Action> =>
    action$.pipe(
      filter(Actions.selectTimesheetFieldOption.match),
      withLatestFrom(state$),
      mergeMap(([action, state]) => of(action.payload)
        .pipe(
          mergeMap(selectTimesheetFieldOptionRpc),
          map(timeGroup => timeGroup.toObject()),
          mergeMap((timeGroup) => concat(
            of(Slice.upsertTimeGroup(timeGroup)),
            of(Actions.generateTimesheetFieldConfigs([timeGroup])),
            of(Actions.listRecentTimesheetFieldOptions(
              new ListRecentTimesheetFieldOptionsRequest().setSiteId(action.payload.getSiteId())
            )),
          )),
          catchError(e => {
            const cacheKey = Slice.createTimesheetFieldCacheKey(action.payload.getSiteId(), action.payload.getTimesheetFieldId())
            const timesheetField = state.timeline.timesheetFieldsCache[cacheKey]
            const fieldName = timesheetField
              ? getTimesheetFieldLabel(timesheetField)
              : t({ id: "msg_timesheet_field", message: "Timesheet field" }).toLowerCase()
            return handleError(e, fieldName)
          }),
        )
      ),
    )

const handleError = (e: unknown, fieldName: string): Observable<Action> => {
  const actions: Action[] = []
  if (isGrpcError(e)) {
    const { code } = e
    if (code === StatusCode.NOT_FOUND) {
      actions.push(
        showSnackBar({
          message: t({ 
            id: "msg_select_timesheet_field_option_not_found", 
            message: "Failed to select {fieldName} value, {fieldName} or value not found.",
            values: { fieldName },
          }),
          messageType: "ERROR",
        }),
        Actions.onTimelineOutOfSync(),
      )
      return concat(actions)
    }
    if (code === StatusCode.PERMISSION_DENIED) {
      actions.push(showSnackBar({
        message: t({ 
          id: "msg_select_timesheet_field_option_permission_denied", 
          message: "Failed to select {fieldName} value. You do not have permission.",
          values: { fieldName },
        }),
        messageType: "ERROR",
      }))
      return concat(actions)
    }
    if (code === StatusCode.FAILED_PRECONDITION) {
      actions.push(
        showSnackBar({
          message: t({ 
            id: "msg_select_timesheet_field_option_failed_precondition", 
            message: "Failed to select {fieldName} value. The timesheet entry has already been posted or is out of sync.",
            values: { fieldName },
          }),
          messageType: "ERROR",
        }),
        Actions.onTimelineOutOfSync(),
      )
      return concat(actions)
    }
  }
  actions.push(showSnackBar({
    message: t({
      id: "msg_select_timesheet_field_option_failed",
      message: "Failed to select {fieldName} value. Please try again in a couple of minutes. If the error persists, please contact support@wisetime.com.",
      values: { fieldName },
    }),
    messageType: "ERROR",
  }))
  return concat(actions)
}

const selectTimesheetFieldOptionRpc = (request: SelectTimesheetFieldOptionRequest): Observable<TimeGroup> =>
  grpc(() => timelineClient.selectTimesheetFieldOption(request, null), never)

export default selectTimesheetFieldOption(selectTimesheetFieldOptionRpc)
